import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useMe } from '../_app';
import { ReasonAction } from '../../components/reason-action';
import { Empty, Facts, Mono, Page, PageHeader, Panel, Skeleton, Table, col } from '@bounded/ui/primitives';
import { ConfirmationChip, ErrorNotice, FreshnessLabel, Provenance } from '@bounded/ui/state';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/devices/$deviceId')({ component: Device });

function Device() {
  const t = useT(); const m = t.devices; const f = useFormat();
  const { deviceId } = Route.useParams();
  const me = useMe();
  const queryClient = useQueryClient();
  const device = useQuery(q.device(deviceId));
  const runtime = useQuery(q.runtime(deviceId));
  const policies = useQuery(q.policies(deviceId));
  const approvals = useQuery(q.approvals(deviceId));
  const [operation, setOperation] = useState<string | null>(null);
  if (device.error) return <Page><ErrorNotice error={device.error} onRetry={() => void device.refetch()} /></Page>;
  if (!device.data) return <Page><Skeleton className="h-64" /></Page>;
  const d = device.data.device;
  const snapshot = runtime.data?.snapshot;

  return (
    <Page>
      <PageHeader title={d.name} description={<Mono value={d.id} head={40} copy />} />
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <Panel title={m.facts}>
          <div className="grid gap-5 px-4 py-4">
            <Facts items={[
              [m.owner, <Link key="o" to="/accounts/$accountId" params={{ accountId: d.user_id }} className="hover:underline"><Mono value={d.user_id} /></Link>],
              [t.labels.created, f.dateTime(d.created_at)],
              [t.labels.status, d.revoked_at ? m.revoked : m.active],
            ]} />
            {d.revoked_at ? <p className="text-sm text-graphite">{m.revokedNote(f.dateTime(d.revoked_at))} <Link to="/operations" className="text-boundary hover:underline">{t.nav.operations}</Link></p> : (
              <ReasonAction danger label={m.revoke} title={m.revokeTitle} consequence={m.revokeConsequence}
                disabled={!me.permissions.mutate} note={!me.permissions.mutate ? t.errorCodes.ADMIN_FORBIDDEN.detail : undefined}
                payload={{ device: d.id, expected_revision: d.revision }}
                run={(reason, key) => api.revokeDevice(d.id, { expected_revision: d.revision, reason }, key)}
                onDone={async (r) => { setOperation(r.operation_id); await Promise.all(['device', 'devices', 'operations', 'audit', 'overview'].map((k) => queryClient.invalidateQueries({ queryKey: [k] }))); }} />
            )}
            {operation && <p role="status" className="text-sm">{t.labels.operationQueued(<Link to="/operations" className="text-boundary hover:underline"><Mono value={operation} /></Link>)}</p>}
          </div>
        </Panel>
        <Panel title={m.runtime}>
          <div className="grid gap-4 px-4 py-4">
            {runtime.error ? <ErrorNotice error={runtime.error} onRetry={() => void runtime.refetch()} />
              : !runtime.data ? <Skeleton className="h-24" />
              : <>
                  <FreshnessLabel freshness={runtime.data.connection} at={runtime.data.received_at} />
                  {!snapshot ? <p className="text-graphite">{m.runtimeEmpty}</p> : <>
                    <Facts items={[
                      [m.daemon, <code key="v">{snapshot.daemon_version}</code>],
                      [m.platform, snapshot.platform],
                      [m.reportDisabled, snapshot.disabled ? <span key="d" className="text-deny">{m.disabledYes}</span> : m.disabledNo],
                      [m.pendingReports, f.number(snapshot.pending_reports)],
                      [m.adapters, snapshot.adapters.length ? <ul key="a" className="grid gap-0.5">{snapshot.adapters.map((a) => <li key={a.id}>{t.agents[a.profile] ?? a.profile} <span className="text-graphite">{m.sessions(a.active_sessions)}</span></li>)}</ul> : t.labels.none],
                    ]} />
                    <Provenance>{m.reportedBy(f.dateTime(snapshot.reported_at))}</Provenance>
                  </>}
                </>}
          </div>
        </Panel>
      </div>
      <Panel title={m.policies}>
        {policies.error ? <div className="p-4"><ErrorNotice error={policies.error} onRetry={() => void policies.refetch()} /></div>
          : !policies.data ? <div className="p-4"><Skeleton className="h-16" /></div>
          : !policies.data.items.length ? <Empty title={m.policiesEmpty} />
          : <Table head={[{ label: m.project }, { label: m.desired, className: col.num }, { label: m.observed, className: col.num }, { label: t.labels.status, className: col.action }]}>
              {policies.data.items.map((p) => (
                <tr key={p.project_id}>
                  <td><Mono value={p.project_id} />{p.reason_code && <div className="text-sm text-graphite"><code>{p.reason_code}</code></div>}</td>
                  <td className={col.num}>{p.generation}</td>
                  <td className={col.num}>{p.observed_generation || '–'}</td>
                  <td className={col.action}><ConfirmationChip state={p.status === 'applied' ? 'confirmed' : p.status}>{m.rollout[p.status]}</ConfirmationChip></td>
                </tr>
              ))}
            </Table>}
      </Panel>
      <Panel title={m.approvals}>
        {approvals.error ? <div className="p-4"><ErrorNotice error={approvals.error} onRetry={() => void approvals.refetch()} /></div>
          : !approvals.data ? <div className="p-4"><Skeleton className="h-16" /></div>
          : !approvals.data.items.length ? <Empty title={m.approvalsEmpty} />
          : <Table head={[{ label: t.labels.id }, { label: m.project, className: col.hideSm }, { label: m.cloud }, { label: m.onDevice }, { label: m.execution, className: col.hideSm }, { label: t.labels.created, className: col.hideMd }]}>
              {approvals.data.items.map((a) => (
                <tr key={a.approval_id}>
                  <td><Mono value={a.approval_id} /></td>
                  <td className={col.hideSm}><Mono value={a.project_id} /></td>
                  <td><code className="text-sm">{a.status}</code></td>
                  <td className="text-graphite"><code className="text-sm">{a.device_status ?? '–'}</code></td>
                  <td className={`${col.hideSm} text-graphite`}><code className="text-sm">{a.execution_status ?? '–'}</code></td>
                  <td className={`${col.hideMd} text-graphite`}>{f.ago(a.created_at)}</td>
                </tr>
              ))}
            </Table>}
      </Panel>
    </Page>
  );
}
